import { useEffect } from 'react';
import { string as zString } from 'zod';
import { init as initBraze } from '@hungryroot/braze';
import { useCustomerExperiments } from '@hungryroot/experiments';
import { useGoogleAnalytics } from '@hungryroot/google-analytics';
import { setUser, setExperiments } from '@hungryroot/o11y';
import { useAuthState, type UseCustomerResponse } from '@hungryroot/rest-hooks';

const PUBLIC_BRAZE_SDK_API_KEY = zString({
  required_error:
    'Environment variable "PUBLIC_BRAZE_SDK_API_KEY" not defined!',
}).parse(process.env.PUBLIC_BRAZE_SDK_API_KEY);

const PUBLIC_BRAZE_SDK_ENDPOINT = zString({
  required_error:
    'Environment variable "PUBLIC_BRAZE_SDK_ENDPOINT" not defined!',
}).parse(process.env.PUBLIC_BRAZE_SDK_ENDPOINT);

/**
 * Initializes customer-related services and tracking when customer data is available.
 *
 * This hook should only be called once at the root of the application to avoid duplicate
 * initialization of third-party services. It performs the following side effects:
 * - Sets Google Analytics userId when customer data is available
 * - Initializes Braze SDK with customer ID
 * - Sets user data in observability (o11y) system
 * - Fetches and sets customer experiment variants
 *
 * @returns {UseCustomerResponse} The customer authentication state, which can be:
 *   - `AuthState.isAuthenticated` with customer data
 *   - `AuthState.isAnonymous` for unauthenticated users
 *   - `AuthState.isLoading` while fetching customer data
 *   - `AuthState.isError` if an error occurred
 */
export function useInitializeCustomer(): UseCustomerResponse {
  const { setGA } = useGoogleAnalytics();
  const auth = useAuthState();
  const { customerData } = auth;

  useEffect(() => {
    if (customerData === undefined) {
      return;
    }

    const customerId = `${customerData.id}`;

    setGA(`userId`, customerId);

    setUser({
      id: customerId,
      name: customerData.first_name || 'undefined',
      email: customerData.email,
    });
  }, [customerData, setGA]);

  useEffect(() => {
    if (customerData === undefined) {
      return;
    }

    initBraze({
      apiKey: PUBLIC_BRAZE_SDK_API_KEY,
      baseUrl: PUBLIC_BRAZE_SDK_ENDPOINT,
      userId: customerData.id,
    });
  }, [customerData]);

  const experiments = useCustomerExperiments();

  useEffect(() => {
    if (experiments.data === undefined) {
      return;
    }

    setExperiments(
      experiments.data.experiments.map(({ experiment, variant }) => {
        return {
          id: experiment,
          variant,
        };
      })
    );
  }, [experiments.data]);

  return auth;
}
